import { useState } from 'react';
import { Zap } from 'lucide-react';
import api from '../../../services/api';
import { useToast } from '../../ui/Toast';
import { usePricingV2 } from '../../../hooks/usePricingV2';
import { autoSyncEnrollmentGate, planErrorMessage } from '../../../billing/pricingV2';

export const toggleAutoSyncDesign = async ({ plan, enrolled, designId, setAutoSyncDesign }) => {
  const enrolling = !enrolled.includes(designId);
  if (enrolling) {
    const gate = autoSyncEnrollmentGate(plan, enrolled, designId);
    if (!gate.allowed) return { status: 'refused', message: gate.message };
  }
  try {
    await setAutoSyncDesign(designId, enrolling);
    return { status: 'saved', message: null };
  } catch (error) {
    return { status: 'error', message: planErrorMessage(error, plan) ?? 'Failed to update auto-sync for this design' };
  }
};

export default function AutoSyncDesignToggle({ designId }) {
  const { enabled, plan, enrolled, reloadOrganization } = usePricingV2();
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  if (!enabled || !plan?.autoSyncDesignLimit) return null;

  const isEnrolled = enrolled.includes(designId);

  const handleClick = async (event) => {
    event.stopPropagation();
    if (saving) return;
    setSaving(true);
    const result = await toggleAutoSyncDesign({ plan, enrolled, designId, setAutoSyncDesign: (id, on) => api.setAutoSyncDesign(id, on) });
    if (result.status === 'saved') await reloadOrganization();
    else if (result.status === 'refused') toast.warning(result.message);
    else toast.error(result.message);
    setSaving(false);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={saving}
      aria-pressed={isEnrolled}
      data-testid="design-auto-sync-toggle"
      data-enrolled={isEnrolled ? 'true' : 'false'}
      className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 squircle-sm text-caption font-medium transition-colors disabled:opacity-60 ${
        isEnrolled
          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
          : 'bg-gray-100 text-gray-700 dark:bg-elevated-dark-2 dark:text-gray-300'
      }`}
    >
      <Zap className="w-3 h-3" />
      {isEnrolled ? 'Auto-sync on' : 'Auto-sync off'}
    </button>
  );
}
